// CPC LOCAL: chain tx totals (RPC) + per-epoch tx chart from checkpoints.

import { formatAmount, formatDate } from '@mysten/core';
import { Heading, LoadingIndicator, Text } from '@mysten/ui';
import { ParentSize } from '@visx/responsive';
import clsx from 'clsx';
import { useMemo } from 'react';

import { AreaGraph } from '~/components/AreaGraph';
import { FormattedStatsAmount } from '~/components/HomeMetrics/FormattedStatsAmount';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { useCpcEpochSummaries } from '~/hooks/useCpcEpochSummaries';
import { useCpcHomeStats } from '~/hooks/useCpcHomeStats';
import { Card } from '~/ui/Card';

type EpochChartPoint = {
	epoch: number;
	epochTotalTransactions: number;
	epochStartTimestamp: number;
};

function TooltipContent({ data }: { data: EpochChartPoint }) {
	const dateFormatted =
		data.epochStartTimestamp > 0
			? formatDate(new Date(data.epochStartTimestamp), ['day', 'month'])
			: `Epoch ${data.epoch}`;
	const totalFormatted = formatAmount(data.epochTotalTransactions);
	return (
		<div className="flex flex-col gap-0.5">
			<Text variant="subtitleSmallExtra/medium" color="steel-darker">
				{dateFormatted}, Epoch {data.epoch}
			</Text>
			<Heading variant="heading6/semibold" color="steel-darker">
				{totalFormatted}
			</Heading>
			<Text variant="subtitleSmallExtra/medium" color="steel-darker" uppercase>
				Transaction Blocks
			</Text>
		</div>
	);
}

export function CpcChainTradesGraph() {
	const stats = useCpcHomeStats();
	const { data: epochData, isPending: epochPending } = useCpcEpochSummaries(31);

	const epochMetrics = useMemo((): EpochChartPoint[] => {
		if (!epochData?.epochs.length) return [];
		return epochData.epochs
			.filter((e) => !e.isCurrent)
			.reverse()
			.map((e) => ({
				epoch: e.epoch,
				epochTotalTransactions: Number(e.epochTotalTransactions),
				epochStartTimestamp: e.epochEndTimestamp ?? 0,
			}));
	}, [epochData?.epochs]);

	const lastEpochTotalTransactions =
		epochMetrics[epochMetrics.length - 1]?.epochTotalTransactions;
	const isLoading = stats.isLoading || epochPending;

	return (
		<Card growOnHover bg="dark" spacing={!epochMetrics.length ? 'lg' : 'lgGraph'} height="full">
			<div className="flex h-full flex-col gap-4 overflow-hidden">
				<div className="flex flex-col gap-1">
					<Heading variant="heading4/semibold" color="dark">
						Transaction Blocks
					</Heading>
					<Text variant="subtitleSmall/medium" color="steel">
						Chain totals from fullnode RPC
					</Text>
				</div>
				<div className="flex flex-wrap gap-6">
					<FormattedStatsAmount
						orientation="vertical"
						label="Total"
						tooltip="Total transaction blocks on chain (fullnode RPC)"
						amount={stats.totalTxBlocks}
						size="md"
						color="dark"
					/>
					<FormattedStatsAmount
						orientation="vertical"
						label="Last Epoch"
						tooltip="Transaction blocks in the most recently completed epoch"
						amount={lastEpochTotalTransactions}
						size="md"
						color="dark"
					/>
					<FormattedStatsAmount
						orientation="vertical"
						label="CLOB Trades"
						tooltip="Total matched trades indexed by Push Hub (separate from chain tx count)"
						amount={stats.tradesTotal}
						size="md"
						color="dark"
					/>
				</div>
				<div
					className={clsx(
						'flex min-h-[180px] flex-1 flex-col items-center justify-center rounded-xl transition-colors',
						!epochMetrics.length && 'bg-white/10',
					)}
				>
					{isLoading ? (
						<div className="flex flex-col items-center gap-1">
							<LoadingIndicator />
							<Text color="dark" variant="body/medium">
								loading chain data
							</Text>
						</div>
					) : epochMetrics.length ? (
						<div className="relative flex-1 self-stretch">
							<ErrorBoundary>
								<ParentSize className="absolute">
									{({ height, width }) => (
										<AreaGraph
											data={epochMetrics}
											height={height}
											width={width}
											getX={({ epoch }) => epoch}
											getY={({ epochTotalTransactions }) => epochTotalTransactions}
											color="yellow"
											formatY={formatAmount}
											tooltipContent={TooltipContent}
										/>
									)}
								</ParentSize>
							</ErrorBoundary>
						</div>
					) : (
						<Text color="dark" variant="body/medium">
							No epoch history available yet
						</Text>
					)}
				</div>
			</div>
		</Card>
	);
}
